import { useEffect, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import api from "@/api/client";
import { Search } from "lucide-react";
import { searchT } from "@/lib/publicI18n";
import AlgoliaHighlight from "@/components/AlgoliaHighlight";

export default function PublicSearchPage() {
  const { slug, basePath = `/s/${slug}`, lang, setLang, primary } = useOutletContext();
  const t = searchT(lang);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState("mongo");
  const [input, setInput] = useState(q);

  useEffect(() => {
    setInput(q);
  }, [q]);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    api.get(`/public/search?space=${encodeURIComponent(slug)}&q=${encodeURIComponent(q)}&lang=all`)
      .then((r) => {
        setResults(r.data.results || []);
        setProvider(r.data.provider || "mongo");
      })
      .finally(() => setLoading(false));
  }, [slug, q]);

  const submit = (e) => {
    e.preventDefault();
    setParams({ q: input });
  };

  const openResult = (r) => {
    const hitLang = r.lang === "ar" ? "ar" : "en";
    if (setLang && hitLang !== lang) {
      setLang(hitLang);
      localStorage.setItem("docs_lang", hitLang);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 fade-in">
      <form onSubmit={submit} className="relative mb-8">
        <Search className="w-5 h-5 absolute ltr:left-4 rtl:right-4 top-1/2 -translate-y-1/2 text-[var(--pp-text-dim)]" />
        <input type="search" value={input} onChange={(e) => setInput(e.target.value)} autoFocus
          placeholder={t.placeholder}
          data-testid="search-page-input"
          className="w-full ltr:pl-12 ltr:pr-4 rtl:pr-12 rtl:pl-4 py-3.5 bg-[var(--pp-surface)] border border-[var(--pp-border)] rounded-full text-[var(--pp-text)] placeholder-[var(--pp-text-dim)] focus:outline-none focus:border-[var(--pp-accent)]"
        />
      </form>

      {!q.trim() ? (
        <div className="text-center text-[var(--pp-text-muted)] py-12">{t.enterTerm}</div>
      ) : loading ? (
        <div className="text-[var(--pp-text-muted)] text-center py-8">{t.searching}</div>
      ) : results.length === 0 ? (
        <div className="text-center py-12" data-testid="search-empty">
          <h3 className="text-lg font-bold mb-2 text-[var(--pp-text)]">{t.noResults} &quot;{q}&quot;</h3>
          <p className="text-[var(--pp-text-muted)] text-sm">{t.noResultsHint}</p>
        </div>
      ) : (
        <>
          <div className="text-sm text-[var(--pp-text-muted)] mb-4" data-testid="search-results-count">
            {results.length} {results.length === 1 ? t.result : t.results} {t.for} <strong>&quot;{q}&quot;</strong>
            {provider === "algolia" && (
              <span className="ml-2 text-[10px] uppercase tracking-wider opacity-60">Algolia · EN + AR</span>
            )}
          </div>
          <div className="space-y-3">
            {results.map((r) => (
              <Link
                key={r.objectID || r.id}
                to={`${basePath}/a/${r.slug}`}
                onClick={() => openResult(r)}
                data-testid={`search-result-${r.slug}`}
                className="block bg-[var(--pp-surface)] border border-[var(--pp-border)] hover:border-[var(--pp-border-hover)] rounded-xl p-4 transition-all"
              >
                <h3 className="font-bold mb-1 text-[var(--pp-text)]" style={{ fontFamily: "Outfit, sans-serif" }}>
                  <AlgoliaHighlight html={r._highlight?.title} text={r.title} query={q} />
                </h3>
                {(r._highlight?.excerpt || r.excerpt) && (
                  <p className="text-sm text-[var(--pp-text-muted)] line-clamp-2">
                    <AlgoliaHighlight html={r._highlight?.excerpt} text={r.excerpt} query={q} />
                  </p>
                )}
                {r._highlight?.body && !r.excerpt && (
                  <p className="text-sm text-[var(--pp-text-dim)] line-clamp-2 mt-1">
                    <AlgoliaHighlight html={r._highlight.body} text="" query={q} />
                  </p>
                )}
                <div className="text-xs text-[var(--pp-text-dim)] mt-2 flex items-center gap-3 flex-wrap">
                  <span>{r.reading_time_minutes} {t.minRead}</span>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[var(--pp-surface-hover)]">
                    {r.lang === "ar" ? "AR" : "EN"}
                  </span>
                  {r.topic_name && (
                    <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: primary || "var(--pp-accent)" }}>
                      {r.topic_name}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
